import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { PAIRING_REASONS, joinNarrative, splitNarrative } from "@/lib/narrative";
import type { AdminDriver, Matchup } from "../types";

const EditMatchupSheet = ({
  open,
  onOpenChange,
  matchup,
  activeDrivers,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchup: Matchup | null;
  activeDrivers: AdminDriver[];
  onSaved: () => void;
}) => {
  const [driverA, setDriverA] = useState(0);
  const [driverB, setDriverB] = useState(0);
  const [reason, setReason] = useState<string>("teammates");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (matchup && open) {
      setDriverA(matchup.driver_a_number);
      setDriverB(matchup.driver_b_number);
      setReason(matchup.pairing_reason ?? "teammates");
      const split = splitNarrative(matchup.narrative);
      setTitle(split.title);
      setBody(split.body);
      setStatus(matchup.status ?? "draft");
    }
  }, [matchup, open]);

  const handleSave = async () => {
    if (!matchup) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc("admin_update_matchup", {
        p_matchup_id: matchup.id,
        p_driver_a: driverA,
        p_driver_b: driverB,
        p_pairing_reason: reason,
        p_narrative: joinNarrative(title, body),
        p_display_order: matchup.display_order ?? 0,
        p_status: status,
      });
      if (error) throw error;
      toast({ title: "Matchup updated" });
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Edit matchup</DrawerTitle>
          <DrawerDescription>Update the pairing, reason, or narrative.</DrawerDescription>
        </DrawerHeader>
        <div className="space-y-4 px-4 pb-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Driver A</Label>
              <Select value={String(driverA)} onValueChange={(v) => setDriverA(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeDrivers.map((d) => (
                    <SelectItem key={d.driver_number} value={String(d.driver_number)}>
                      {d.name_acronym} — {d.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Driver B</Label>
              <Select value={String(driverB)} onValueChange={(v) => setDriverB(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeDrivers.map((d) => (
                    <SelectItem key={d.driver_number} value={String(d.driver_number)}>
                      {d.name_acronym} — {d.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Pairing reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAIRING_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Headline</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DrawerFooter>
          <Button className="w-full font-semibold" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default EditMatchupSheet;
